import { useMemo } from 'react'
import { ONBOARDING_RAIL } from '@forwardflow/constants/onboarding.constants'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { TOnboardingStep } from '@forwardflow/types/onboarding'
import { conversationsFor } from '../budget-presets'
import { FF_ASSISTANT_NUMBER_PRETTY } from '../dev/devTools'
import { stopItems } from '../steps/stage-mapping-explained'
import { seatCostFor } from '../steps/you-and-team/seats'
import { fullName } from '../steps/you-and-team/seats'
import { listSentence, plural, prettyPhone } from './format'

export type TRailState = 'done' | 'current' | 'upcoming'

export interface IRailSummary {
   id: string
   title: string
   ask: string
   steps: TOnboardingStep[]
   state: TRailState
   /** What was decided, once done (or so far, while current). */
   lines: string[]
   /** Can the customer jump back here? */
   clickable: boolean
}

/**
 * One line per step for the left-hand rail: what has been decided so far,
 * read straight from the setup record, so a customer can see the whole
 * picture at any point without leaving the current screen.
 */
export const useSetupSummary = (): {
   rail: IRailSummary[]
   currentIndex: number
   stepNumber: number
   total: number
} => {
   const { onboardingData } = useOnboardingStoreV2()
   const {
      currentStep,
      isFUBConnected,
      isSubscriptionActivated,
      isCalendarConnected,
      invitedUsers,
      pricingData,
      campaignConfiguration,
      stageMappings,
      businessDetails,
      isOnBoardingCompleted,
   } = onboardingData

   return useMemo(() => {
      const currentRail = ONBOARDING_RAIL.findIndex((r) => r.steps.includes(currentStep))
      const owner = invitedUsers?.find((u) => u.isOwner)
      const members = (invitedUsers || []).filter((u) => !u.isOwner)
      const seats = pricingData?.seatsCount || 1 + members.length
      const codes = campaignConfiguration?.area_codes || []
      const stops = stopItems(stageMappings || [])

      const linesFor = (id: string, state: TRailState): string[] => {
         if (state === 'upcoming') return []
         switch (id) {
            case 'connect':
               return isFUBConnected ? ['Connected'] : []
            case 'you': {
               if (!owner) return []
               const out = [fullName(owner)]
               if (owner.phone) out.push(prettyPhone(owner.phone))
               out.push(
                  members.length
                     ? `+ ${plural(members.length, 'teammate')} · $${seatCostFor(seats)}/mo`
                     : `Just you · $${seatCostFor(1)}/mo`
               )
               return out
            }
            case 'plan': {
               const budget = pricingData?.maxSpend ?? 150
               const out = [`$${budget}/mo · about ${conversationsFor(budget)} leads`]
               if (isSubscriptionActivated) out.push('Card on file')
               return out
            }
            case 'area':
               return codes.length ? [codes.map((c) => `(${c})`).join(', ')] : []
            case 'number': {
               const out = [FF_ASSISTANT_NUMBER_PRETTY]
               out.push(isCalendarConnected ? 'Calendar connected' : 'Books into Follow Up Boss')
               return out
            }
            case 'stops':
               return stops.length
                  ? [
                       plural(stops.length, 'stage or tag', 'stages and tags'),
                       listSentence(stops.slice(0, 3).map((s) => s.name)) +
                          (stops.length > 3 ? ` +${stops.length - 3}` : ''),
                    ]
                  : []
            case 'business': {
               if (businessDetails?.businessRegistrationNumber || isOnBoardingCompleted)
                  return ['Sent for texting approval']
               if (businessDetails?.legalBusinessName) return [businessDetails.legalBusinessName]
               return []
            }
            default:
               return []
         }
      }

      const rail: IRailSummary[] = ONBOARDING_RAIL.map((r, i) => {
         const state: TRailState =
            i < currentRail ? 'done' : i === currentRail ? 'current' : 'upcoming'
         const paidLocked =
            isSubscriptionActivated && (r.id === 'connect' || r.id === 'plan')
         return {
            id: r.id,
            title: r.title,
            ask: r.ask,
            steps: r.steps,
            state,
            lines: linesFor(r.id, state),
            clickable: state === 'done' && !paidLocked,
         }
      })

      return {
         rail,
         currentIndex: currentRail,
         stepNumber: Math.max(1, currentRail + 1),
         total: ONBOARDING_RAIL.length,
      }
   }, [
      currentStep,
      isFUBConnected,
      isSubscriptionActivated,
      isCalendarConnected,
      invitedUsers,
      pricingData,
      campaignConfiguration,
      stageMappings,
      businessDetails,
      isOnBoardingCompleted,
   ])
}
